import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/api/client'

interface AuthGuardProps {
  children: React.ReactNode
}

/**
 * Authentication guard that checks for Supabase session.
 * If no session is found, redirects to /auth page.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  const navigate = useNavigate()
  const [isChecking, setIsChecking] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    const checkAuth = async () => {
      // Use getUser() to validate token is still valid
      const { data: { user }, error } = await supabase.auth.getUser()

      if (error || !user) {
        navigate('/auth')
        setIsAuthenticated(false)
      } else {
        setIsAuthenticated(true)
      }
      setIsChecking(false)
    }

    checkAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate('/auth')
        setIsAuthenticated(false)
      } else {
        setIsAuthenticated(true)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [navigate])

  // Show nothing while checking auth
  if (isChecking || !isAuthenticated) {
    return null
  }

  return <>{children}</>
}
